//@flow
import {useCallback, useEffect, useReducer, useState} from 'react';
import {fetchAdmin1, fetchAdmin2, fetchAdmin3} from '../../../utils/location';

import {useStoreCountry} from '../../country/utils';

type AdminAddress = {
  adminId: number,
  adminName: string,
  type: 'admin1' | 'admin2' | 'admin3',
};
type AddressState = {
  admin1List: AdminAddress[],
  admin2List: AdminAddress[],
  admin3List: AdminAddress[],

  selectedAdmin1?: AdminAddress,
  selectedAdmin2?: AdminAddress,
  selectedAdmin3?: AdminAddress,

  loading: boolean,
};

type Action =
  | {type: 'SET_ADMIN1_LIST', payload: AdminAddress[]}
  | {type: 'SET_ADMIN2_LIST', payload: AdminAddress[]}
  | {type: 'SET_ADMIN3_LIST', payload: AdminAddress[]}
  | {type: 'SET_SELECTED_ADMIN1', payload: AdminAddress}
  | {type: 'SET_SELECTED_ADMIN2', payload: AdminAddress}
  | {type: 'SET_SELECTED_ADMIN3', payload: AdminAddress}
  | {type: 'SET_LOADING', payload: boolean}
  | {
      type: 'SET_ADMINS',
      payload: {
        admin2List?: AdminAddress[],
        admin3List?: AdminAddress[],

        selectedAdmin1?: AdminAddress,
        selectedAdmin2?: AdminAddress,
        selectedAdmin3?: AdminAddress,
      },
    };

const ACTIONS = {
  SET_ADMIN1_LIST: 'SET_ADMIN1_LIST',
  SET_ADMIN2_LIST: 'SET_ADMIN2_LIST',
  SET_ADMIN3_LIST: 'SET_ADMIN3_LIST',
  SET_SELECTED_ADMIN1: 'SET_SELECTED_ADMIN1',
  SET_SELECTED_ADMIN2: 'SET_SELECTED_ADMIN2',
  SET_SELECTED_ADMIN3: 'SET_SELECTED_ADMIN3',
  SET_LOADING: 'SET_LOADING',
  SET_ADMINS: 'SET_ADMINS',
};

const initialState: AddressState = {
  admin1List: [],
  admin2List: [],
  admin3List: [],
  loading: false,
};

function reducer(state: AddressState, action: Action): AddressState {
  switch (action.type) {
    case 'SET_ADMIN1_LIST':
      return {...state, admin1List: action.payload};
    case 'SET_ADMIN2_LIST':
      return {...state, admin2List: action.payload};
    case 'SET_ADMIN3_LIST':
      return {...state, admin3List: action.payload};
    case 'SET_SELECTED_ADMIN1':
      return {
        ...state,
        selectedAdmin1: action.payload,
        selectedAdmin2: undefined,
        selectedAdmin3: undefined,
      };
    case 'SET_SELECTED_ADMIN2':
      return {
        ...state,
        selectedAdmin2: action.payload,
        selectedAdmin3: undefined,
      };
    case 'SET_SELECTED_ADMIN3':
      return {...state, selectedAdmin3: action.payload};

    case 'SET_ADMINS': {
      //$FlowFixMe
      return {...state, ...action.payload};
    }

    default:
      return state;
  }
}

type UseAdminOptions = {
  ignoreStoreCountry?: boolean,
};

export function useAdminAddress(
  countryProp?: string,
  options?: UseAdminOptions = {}
) {
  const {ignoreStoreCountry} = options;
  const country = useStoreCountry();
  const [state, dispatch] = useReducer(reducer, initialState);
  const [loading, setLoading] = useState(false);

  const {selectedAdmin1, selectedAdmin2} = state;
  const countryCode = ignoreStoreCountry ? countryProp : country?.code;

  useEffect(() => {
    if (!countryCode) return;
    const controller = new AbortController();
    const {signal} = controller;

    setLoading(true);
    fetchAdmin1(countryCode, signal)
      .then(admin2Response => {
        const admin1List = admin2Response.map(admin1 => ({
          adminId: admin1.admin1Id,
          adminName: admin1.admin1Name,
          type: 'admin1',
        }));
        dispatch({type: ACTIONS.SET_ADMIN1_LIST, payload: admin1List});
      })
      .catch(error => {
        console.error(`Error fetching admin1 for country: ${error}`);
      })
      .finally(() => {
        setLoading(false);
      });

    return () => {
      controller.abort();
    };
  }, [countryCode]);

  useEffect(() => {
    if (!countryCode) return;
    if (!selectedAdmin1) return;

    const controller = new AbortController();
    const {signal} = controller;

    const {adminId} = selectedAdmin1;

    setLoading(true);
    fetchAdmin2(countryCode, adminId.toString(), signal)
      .then(admin2Response => {
        const admin2List = admin2Response.map(admin2 => ({
          adminId: admin2.admin2Id,
          adminName: admin2.admin2Name,
          type: 'admin2',
        }));
        dispatch({type: ACTIONS.SET_ADMIN2_LIST, payload: admin2List});
      })
      .catch(error => {
        console.error(`Error fetching admin2 for country: ${error}`);
      })
      .finally(() => {
        setLoading(false);
      });
    return () => {
      controller.abort();
    };
  }, [countryCode, selectedAdmin1]);

  useEffect(() => {
    if (!selectedAdmin2) return;
    if (!countryCode) return;

    const controller = new AbortController();
    const {signal} = controller;
    if (!selectedAdmin2 || !selectedAdmin1) return;

    const {adminId: adminId2} = selectedAdmin2;
    const {adminId: adminId1} = selectedAdmin1;

    setLoading(true);

    fetchAdmin3(countryCode, adminId1.toString(), adminId2.toString(), signal)
      .then(admin2Response => {
        const admin3List = admin2Response.map(admin3 => ({
          adminId: admin3.admin3Id,
          adminName: admin3.admin3Name,
          type: 'admin3',
        }));
        dispatch({type: ACTIONS.SET_ADMIN3_LIST, payload: admin3List});
      })
      .catch(error => {
        console.error(`Error fetching admin3 for country: ${error}`);
      })
      .finally(() => {
        setLoading(false);
      });
    return () => {
      controller.abort();
    };
  }, [countryCode, selectedAdmin1, selectedAdmin2]);

  const selectAdmin1 = useCallback((admin1: AdminAddress) => {
    dispatch({type: ACTIONS.SET_SELECTED_ADMIN1, payload: admin1});
  }, []);

  const selectAdmin2 = useCallback((admin2: AdminAddress) => {
    dispatch({type: ACTIONS.SET_SELECTED_ADMIN2, payload: admin2});
  }, []);

  const selectAdmin3 = useCallback((admin3: AdminAddress) => {
    dispatch({type: ACTIONS.SET_SELECTED_ADMIN3, payload: admin3});
  }, []);

  const resetSelectedAdmins = useCallback(() => {
    dispatch({
      type: ACTIONS.SET_ADMINS,
      payload: {
        selectedAdmin1: undefined,
        selectedAdmin2: undefined,
        selectedAdmin3: undefined,
        admin2List: [],
        admin3List: [],
      },
    });
  }, []);

  const setAdmins = useCallback(
    async (
      admin1IdOrName: string,
      admin2IdOrName?: string,
      admin3IdOrName?: string
    ) => {
      if (!countryCode) return;
      if (!admin1IdOrName) return;

      const admin1 = state.admin1List.find(
        admin1 =>
          admin1.adminName === admin1IdOrName ||
          admin1.adminId.toString() === admin1IdOrName
      );

      if (!admin1) return;
      const controller = new AbortController();
      const {signal} = controller;
      try {
        const admin2Response = await fetchAdmin2(
          countryCode,
          admin1.adminId.toString(),
          signal
        );
        const admin2List = admin2Response.map(admin2 => ({
          adminId: admin2.admin2Id,
          adminName: admin2.admin2Name,
          type: 'admin2',
        }));

        const admin2 = admin2List.find(
          admin2 =>
            admin2.adminName === admin2IdOrName ||
            admin2.adminId.toString() === admin2IdOrName
        );

        if (!admin2) {
          dispatch({
            type: ACTIONS.SET_ADMINS,
            payload: {
              selectedAdmin1: admin1,
              selectedAdmin2: undefined,
              selectedAdmin3: undefined,
              admin2List,
              admin3List: [],
            },
          });
          return;
        }

        const admin3Response = await fetchAdmin3(
          countryCode,
          admin1.adminId.toString(),
          admin2.adminId.toString(),
          signal
        );

        const admin3List = admin3Response.map(admin3 => ({
          adminId: admin3.admin3Id,
          adminName: admin3.admin3Name,
          type: 'admin3',
        }));

        const admin3 = admin3List.find(
          admin3 =>
            admin3.adminName === admin3IdOrName ||
            admin3.adminId.toString() === admin3IdOrName
        );

        if (!admin3) {
          dispatch({
            type: ACTIONS.SET_ADMINS,
            payload: {
              selectedAdmin1: admin1,
              selectedAdmin2: admin2,
              selectedAdmin3: undefined,
              admin2List,
              admin3List,
            },
          });
          return;
        }

        dispatch({
          type: ACTIONS.SET_ADMINS,
          payload: {
            selectedAdmin1: admin1,
            selectedAdmin2: admin2,
            selectedAdmin3: admin3,
            admin2List,
            admin3List,
          },
        });
      } catch (error) {
        console.error(`Error fetching all admins for country: ${error}`);
        controller.abort();
      }
    },
    [countryCode, state.admin1List]
  );

  return {
    ...state,
    selectAdmin1,
    selectAdmin2,
    selectAdmin3,
    setAdmins,
    resetSelectedAdmins,
    loading,
  };
}
